/**
 * PropositionsActivityChart — Dashboard "Activité signatures" (V36.15).
 *
 * Graphique mensuel : envoyées vs signées vs validées + KPIs de conversion.
 * Accessible Super Admin / Admin / Cooper (cooper voit ses propres données).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  Loader2, Send, FileSignature, CheckCircle2, XCircle, TrendingUp, RefreshCw, BellRing,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { getAuthHeader } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { value: 3, label: '3 mois' },
  { value: 6, label: '6 mois' },
  { value: 12, label: '12 mois' },
  { value: 24, label: '24 mois' },
];

const KpiCard = ({ icon: Icon, label, value, suffix, color, sub }) => (
  <div className={`rounded-xl p-4 border ${color}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4" />
      <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
    </div>
    <div className="text-2xl font-extrabold leading-none">
      {value}
      {suffix && <span className="text-base font-medium ml-1">{suffix}</span>}
    </div>
    {sub && <div className="text-[11px] mt-1 opacity-80">{sub}</div>}
  </div>
);

export default function PropositionsActivityChart() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/propositions-signees/activity`,
        { params: { months }, headers: getAuthHeader() },
      );
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          Chargement des statistiques…
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-red-600 text-sm">
          Impossible de charger les statistiques.
        </CardContent>
      </Card>
    );
  }

  const { series, totals, kpis } = data;
  const hasData = totals.envoyees > 0 || totals.signees > 0;

  return (
    <Card data-testid="propositions-activity-chart">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-indigo-600" />
              Activité signatures
            </CardTitle>
            <CardDescription className="mt-1 text-xs">
              Suivi mensuel — envoyées vs signées vs validées + KPIs de conversion.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setMonths(p.value)}
                  className={`text-xs font-semibold px-2.5 py-1.5 rounded-md transition-all ${
                    months === p.value
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`activity-period-${p.value}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              data-testid="activity-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <KpiCard
            icon={Send}
            label="Envoyées"
            value={totals.envoyees}
            color="bg-blue-50 border-blue-200 text-blue-900"
          />
          <KpiCard
            icon={FileSignature}
            label="Signées"
            value={totals.signees}
            color="bg-purple-50 border-purple-200 text-purple-900"
            sub={
              totals.envoyees > 0
                ? `${kpis.taux_signature}% taux de signature`
                : null
            }
          />
          <KpiCard
            icon={CheckCircle2}
            label="Validées"
            value={totals.validees}
            color="bg-emerald-50 border-emerald-200 text-emerald-900"
            sub={
              totals.signees > 0
                ? `${kpis.taux_validation}% des signées`
                : null
            }
          />
          <KpiCard
            icon={XCircle}
            label="Refusées"
            value={totals.refusees}
            color="bg-red-50 border-red-200 text-red-900"
            sub={
              totals.signees > 0
                ? `${kpis.taux_refus}% des signées`
                : null
            }
          />
          <KpiCard
            icon={BellRing}
            label="Relances"
            value={totals.relances}
            color="bg-amber-50 border-amber-200 text-amber-900"
            sub="emails auto J+7"
          />
        </div>

        {/* Chart */}
        {hasData ? (
          <div className="h-72 w-full" data-testid="activity-chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={series}
                margin={{ top: 12, right: 12, bottom: 0, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke="#64748b"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: '#cbd5e1' }}
                />
                <YAxis
                  stroke="#64748b"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(15, 23, 42, 0.95)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#fff',
                  }}
                  itemStyle={{ color: '#fff' }}
                  labelStyle={{ color: '#fff', fontWeight: 700 }}
                />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                />
                <Bar
                  dataKey="envoyees"
                  name="Envoyées"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={36}
                />
                <Bar
                  dataKey="signees"
                  name="Signées"
                  fill="#7c3aed"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={36}
                />
                <Line
                  type="monotone"
                  dataKey="validees"
                  name="Validées"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                  activeDot={{ r: 6 }}
                />
                {totals.refusees > 0 && (
                  <Line
                    type="monotone"
                    dataKey="refusees"
                    name="Refusées"
                    stroke="#dc2626"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={{ r: 3 }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-12 px-6 bg-slate-50 rounded-lg border border-dashed border-slate-200">
            <TrendingUp className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-semibold text-slate-700">
              Aucune activité de signature sur cette période.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Les statistiques apparaîtront dès la première proposition envoyée pour signature.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
